import { useState } from 'react';
import { useClerk, useUser } from '@clerk/clerk-react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { startCheckout } from '@/lib/checkout';

// Backwards-compatible alias kept so older imports (if any) keep working.
export function startCheckoutPlaceholder() {
  // eslint-disable-next-line no-console
  console.log('startCheckoutPlaceholder is deprecated; use startCheckout(priceId)');
}

const PLANS = [
  {
    id: 'monthly',
    title: 'Monthly',
    price: '$5',
    cadence: '/ month',
    note: '',
  },
  {
    id: 'annual',
    title: 'Annual',
    price: '$50',
    cadence: '/ year',
    note: '(2 months free)',
  },
];

function PricingCard({ plan, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(plan.id)}
      data-testid={`upgrade-plan-${plan.id}`}
      aria-pressed={selected}
      className={cn(
        'group flex flex-col items-start text-left',
        'rounded-2xl border bg-card transition-all duration-150',
        'p-8 sm:p-10 min-h-[180px]',
        'hover:border-foreground/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground/40',
        selected
          ? 'border-foreground/80 shadow-[0_0_0_1px_rgba(0,0,0,0.6)] dark:shadow-[0_0_0_1px_rgba(255,255,255,0.5)]'
          : 'border-border',
      )}
    >
      <p className="text-sm font-medium tracking-wide uppercase text-muted-foreground">
        {plan.title}
      </p>
      <p className="mt-4 font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-foreground">
        {plan.price}
        <span className="ml-1 text-base font-normal text-muted-foreground">
          {plan.cadence}
        </span>
      </p>
      {plan.note && (
        <p className="mt-2 text-sm text-muted-foreground">{plan.note}</p>
      )}
    </button>
  );
}

// Inner UI body — pure presentational. Receives an onSignIn callback so the
// caller can decide whether to open Clerk or navigate.
function UpgradeModalBody({ open, onOpenChange, mode, onSignIn, onCheckout }) {
  const [selected, setSelected] = useState('monthly');
  const [submitting, setSubmitting] = useState(false);

  const handlePrimary = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onCheckout(selected);
    } finally {
      setSubmitting(false);
    }
  };

  const isPricing = mode === 'pricing';
  const headline = isPricing ? 'Pricing options' : 'Save your forecast';
  const subheadline = isPricing
    ? null
    : 'Keep your work safe. Come back anytime. Unlimited forecasting.';
  const primaryLabel = isPricing ? 'Upgrade' : 'Continue to Checkout';
  const microcopy = isPricing
    ? 'No tiers. No limits. Cancel anytime.'
    : 'You can keep using Guest Mode for free. Saving requires a subscription.';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[640px] p-0 overflow-hidden"
        data-testid="upgrade-modal"
        data-mode={mode}
      >
        <div className="relative px-8 sm:px-12 py-10 sm:py-14">
          <h2
            data-testid="upgrade-modal-headline"
            className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-center"
          >
            {headline}
          </h2>
          {subheadline && (
            <p
              data-testid="upgrade-modal-subheadline"
              className="mt-3 text-center text-base text-muted-foreground max-w-md mx-auto"
            >
              {subheadline}
            </p>
          )}

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {PLANS.map((p) => (
              <PricingCard
                key={p.id}
                plan={p}
                selected={selected === p.id}
                onSelect={setSelected}
              />
            ))}
          </div>

          <div className="mt-8 flex flex-col items-center gap-3">
            <Button
              size="lg"
              onClick={handlePrimary}
              disabled={submitting}
              data-testid="upgrade-primary-cta"
              className="w-full sm:w-auto bg-black text-white hover:bg-black/85 text-base px-10 py-5 rounded-md disabled:opacity-60"
            >
              {submitting ? 'Redirecting…' : primaryLabel}
            </Button>
            <button
              type="button"
              onClick={onSignIn}
              data-testid="upgrade-secondary-cta"
              className="text-sm text-muted-foreground hover:text-foreground hover:underline transition-colors"
            >
              Sign In
            </button>
          </div>

          <p
            data-testid="upgrade-microcopy"
            className="mt-6 text-center text-xs text-muted-foreground max-w-md mx-auto"
          >
            {microcopy}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Clerk-aware wrapper. Only mount when ClerkProvider is in the tree.
function ClerkUpgradeModal(props) {
  const { openSignIn, openSignUp } = useClerk();
  const { isLoaded, isSignedIn } = useUser();

  const handleSignIn = () => {
    // Already-signed-in users hitting the secondary "Sign In" link should
    // just go to /app — openSignIn() is a no-op for them.
    if (isLoaded && isSignedIn) {
      if (props.onOpenChange) props.onOpenChange(false);
      window.location.href = '/app';
      return;
    }
    if (props.onOpenChange) props.onOpenChange(false);
    setTimeout(() => {
      openSignIn({ afterSignInUrl: '/app', afterSignUpUrl: '/app' });
    }, 60);
  };

  // Clerk-first → Stripe flow.
  // If the user is signed in, go straight to Stripe.
  // If not, send them through Clerk Sign Up first; Clerk will then redirect
  // them to /checkout-resume?priceId=..., which finishes the hand-off to
  // Stripe with explicit visible feedback (and a retry button if anything
  // goes wrong). Returning subscribers can still pick "Sign in instead" from
  // the link inside Clerk's Sign Up modal.
  //
  // We close our own modal *before* opening Clerk's, otherwise Radix's focus
  // trap holds onto pointer events and Clerk's modal feels frozen until the
  // user clicks once to dismiss our overlay.
  const handleCheckout = async (priceId) => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      if (props.onOpenChange) props.onOpenChange(false);
      const dest = `/checkout-resume?priceId=${encodeURIComponent(priceId)}`;
      // Tiny delay so Radix's exit animation runs before Clerk mounts on top.
      setTimeout(() => {
        openSignUp({
          afterSignInUrl: dest,
          afterSignUpUrl: dest,
        });
      }, 60);
      return;
    }
    await startCheckout(priceId);
  };

  return <UpgradeModalBody {...props} onSignIn={handleSignIn} onCheckout={handleCheckout} />;
}

// Dev-mode wrapper. Used when running without valid Clerk keys; the
// "Sign In" link simply routes to /app where the dev-mode dashboard
// is shown. The checkout call still hits the backend and either
// succeeds (if Stripe + auth bypass are configured) or surfaces an error.
function DevUpgradeModal(props) {
  const handleSignIn = () => {
    window.location.href = '/app';
  };
  const handleCheckout = async (priceId) => {
    await startCheckout(priceId);
  };
  return <UpgradeModalBody {...props} onSignIn={handleSignIn} onCheckout={handleCheckout} />;
}

export default function UpgradeModal({ devMode = false, ...rest }) {
  return devMode ? <DevUpgradeModal {...rest} /> : <ClerkUpgradeModal {...rest} />;
}
